import {
  Button,
  CopyMdIcon,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  RemoveSmIcon,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useGetUsers, {
  UserSubscription,
} from 'hooks/services/subscription/useGetUsers';
import useCreateList, { ICreateList } from 'hooks/services/lists/useCreateList';
import { TeamsUsersSearch } from 'pages/settings/Subscription/components/TeamsUsersSearch';
import usePatchList, { IPatchList } from 'hooks/services/lists/usePatchList';
import usePatchUserPreferences from 'hooks/services/user/usePatchUserPreferences';
import { useAuth } from 'context/auth';
import { List, ListType } from 'crono-fe-common/types/list';
import { useAnalytics } from 'context/analytics';
import { withMeta } from 'crono-fe-common/types/mutationMeta';
import {
  copyListShareLink,
  getListShareUrl,
  isPrivateList,
} from 'pages/lists/listSharing';

const MISSING_SHARED_USERS_MESSAGE =
  'Pick at least one teammate to share this list with, or switch it back to private.';

interface ListModalProps {
  type: ListType;
  onClose: () => void;
  onCreate?: (listId: number) => void;
  forEdit?: boolean; //the modal is for create by default
  listToEdit?: List;
  precompletedName?: string;
  onDelete?: () => void;
  addToListDirectly?: boolean;
  handleProspectAccountClick?: (list: List) => void;
  handleAddDirectlyToDropdown?: (list: List) => void;
}

const ListModal = ({
  type,
  onClose,
  onCreate,
  forEdit,
  listToEdit,
  precompletedName,
  onDelete,
  addToListDirectly,
  handleProspectAccountClick,
  handleAddDirectlyToDropdown,
}: ListModalProps) => {
  const { user } = useAuth();
  const [listName, setListName] = useState<string>('');
  const [sharedOpen, setSharedOpen] = useState<boolean>(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const { mutateAsync: createList, isLoading: isCreateLoading } =
    useCreateList();
  const { mutateAsync: patchList, isLoading: isPatchLoading } = usePatchList();
  const { data: usersData } = useGetUsers();
  const { mutateAsync: patchUserPreferencesAsync } = usePatchUserPreferences();
  const listNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (precompletedName?.length && !listName) setListName(precompletedName);
  }, [precompletedName]);

  const users: UserSubscription[] = useMemo(() => {
    const allUsers = usersData?.data?.data ?? [];
    return allUsers.filter((u) => u.id !== user?.id);
  }, [usersData, user]);

  const analytics = useAnalytics();

  useEffect(() => {
    if (!listToEdit) return;
    const { name, sharedUsers } = listToEdit;

    setListName(name);

    if (!isPrivateList(listToEdit)) {
      setSharedOpen(true);
      setSelectedUserIds(sharedUsers?.map((user) => user.id) ?? []);
    }
  }, [listToEdit]);

  // A list shared with the whole team can come back without its sharedUsers;
  // picking everyone keeps it shared with the team when it is saved
  useEffect(() => {
    if (listToEdit?.shared && !listToEdit.sharedUsers?.length && users.length) {
      setSelectedUserIds(users.map((teammate) => teammate.id));
    }
  }, [listToEdit, users]);

  const handleSwitchToggle = useCallback(() => {
    setSharedOpen((prev) => !prev);
    setSelectedUserIds([]);
  }, []);

  const allUsersChecked: boolean = useMemo(() => {
    return selectedUserIds.length === users.length;
  }, [selectedUserIds, users]);

  // A list that isn't private has to be shared with somebody
  const missingSharedUsers = sharedOpen && selectedUserIds.length === 0;

  const handleSaveList = useCallback(async () => {
    try {
      if (!forEdit) {
        const createPayload: ICreateList = {
          name: listName,
          type,
          shared: allUsersChecked,
          sharedUsersIds: selectedUserIds,
        };

        const createdListResp = await createList(
          withMeta(createPayload, {
            successMessage: 'Successfully created list',
            errorMessage: 'Error while creating list',
          }),
        );
        const createdList: List = createdListResp.data.data;

        if (analytics) {
          analytics.track('create-list', {
            listId: createdList.name,
            listType: createdList.type,
          });
        }

        const updatedOpenLists: number[] = [
          createdList.id,
          ...(user?.userPreferences.openListsIds || []),
        ];

        if (addToListDirectly && handleProspectAccountClick) {
          handleProspectAccountClick(createdList);
        }
        if (handleAddDirectlyToDropdown) {
          handleAddDirectlyToDropdown(createdList);
        }
        if (user) {
          user.userPreferences.openListsIds = updatedOpenLists;
        }

        await patchUserPreferencesAsync({ openListsIds: updatedOpenLists });

        if (onCreate) {
          onCreate(createdList.id);
        }
      } else {
        if (!listToEdit) return;

        const patchPayload: IPatchList = {
          id: listToEdit.id,
          name: listName,
          shared: allUsersChecked,
          sharedUsersIds: selectedUserIds,
        };

        await patchList(
          withMeta(patchPayload, {
            successMessage: 'Successfully updated list',
            errorMessage: 'Error while updating list',
          }),
        );
      }

      onClose();
    } catch (error) {
      console.error(error);
    }
  }, [
    createList,
    patchList,
    listName,
    listToEdit,
    type,
    selectedUserIds,
    allUsersChecked,
    forEdit,
  ]);

  const saveButton = (
    <Button
      className="min-w-33"
      size="lg"
      disabled={!listName || missingSharedUsers}
      loading={isCreateLoading || isPatchLoading}
      onClick={handleSaveList}
    >
      Save
    </Button>
  );

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent
        className="max-w-131"
        portalClassName="z-9999999"
        initialFocus={listNameInputRef}
      >
        <DialogHeader>
          <DialogTitle>{forEdit ? 'Edit list:' : 'Create list'}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold" htmlFor="listName">
              List name
            </label>

            <Input
              ref={listNameInputRef}
              id="listName"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder="Insert list name..."
              autoComplete="off"
            />
          </div>

          <div className="flex items-center gap-2">
            <label
              htmlFor="private"
              className="text-xs font-medium text-gray-700"
            >
              Private
            </label>

            <Switch
              id="private"
              checked={!sharedOpen}
              onCheckedChange={handleSwitchToggle}
            />
          </div>
        </div>

        {/* 24px apart: the dialog's own 16px gap plus mt-2 */}
        {sharedOpen && (
          <div className="mt-2 flex min-h-0 flex-col gap-6">
            <div className="h-px shrink-0 bg-neutral-300" />

            {listToEdit && (
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 flex-col">
                  <span className="text-sm font-semibold">Share link</span>
                  <span className="truncate text-xs text-gray-700">
                    {getListShareUrl(listToEdit.id)}
                  </span>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="px-2"
                  startIcon={CopyMdIcon}
                  onClick={() => copyListShareLink(listToEdit.id)}
                >
                  Copy
                </Button>
              </div>
            )}

            <div className="flex max-h-75 flex-col overflow-hidden">
              <TeamsUsersSearch
                selectedUserIds={selectedUserIds}
                onChangeSelectedUserIds={setSelectedUserIds}
              />
            </div>
          </div>
        )}

        <DialogFooter className="mt-6 items-center">
          {forEdit && onDelete && listToEdit?.owned && (
            <Button
              variant="text"
              tone="red"
              size="sm"
              endIcon={RemoveSmIcon}
              onClick={onDelete}
            >
              Delete list
            </Button>
          )}

          <div className="ml-auto flex items-center gap-2">
            <Button
              className="min-w-33"
              variant="outline"
              size="lg"
              onClick={onClose}
            >
              Cancel
            </Button>

            {missingSharedUsers ? (
              <Tooltip>
                {/* A disabled button gets no pointer events, so the wrapper
                    carries the tooltip */}
                <TooltipTrigger asChild>
                  <span tabIndex={0}>{saveButton}</span>
                </TooltipTrigger>
                <TooltipContent side="top" className="z-999999999 max-w-60">
                  {MISSING_SHARED_USERS_MESSAGE}
                </TooltipContent>
              </Tooltip>
            ) : (
              saveButton
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ListModal;
